import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { FeedStamp } from './FeedStamp.js';
import {
  aBlancEquivalents,
  assignEmployeeColors,
  metricColor,
  buildRegistry,
  checkInTime,
  draftBoardDay,
  evaluateMetric,
  formatMetricValue,
  chipLabel,
  computeActivityCoverage,
  computeActivityPresence,
  computeDayWorkedHours,
  businessDayFetchRange,
  dayForecasts,
  frDayLabel,
  nextOccupancy,
  slotsOverlappingWindow,
  profileOutputsForDay,
  staffingConfigFromRows,
  summaryMetrics,
  todayIsoParis,
  workloadFromCounts,
  workloadFromDay,
  type DayForecast,
  type MetricDef,
  type StaffingConfigSnapshot,
  type ActivityCoverage,
  type ActivityOverride,
  type ActivityPresence,
  type ActivityQualification,
  type BoardActivity,
  type BoardDay,
  type DayPresence,
  type Employee,
} from '@lemontaigu/staff';
import {
  useBoard,
  useBoardDemand,
  useBoardPresence,
  useBoardRealtime,
  useMetricRegistry,
  useStaffingConfig,
  useTimesheet,
} from '@lemontaigu/staff/react';
import { useCapability } from '@lemontaigu/platform-ui';
import { CAPABILITIES } from '@lemontaigu/platform';
import { FrDateInput } from '../ui/FrDateInput.js';
import { broadcastGaps } from './broadcast.js';
import { AssignEditor, DeclareEditor } from './coverageEditors.js';
import { DayGantt } from './DayGantt.js';
import { PrevisionnelStrip } from './PrevisionnelStrip.js';
import { PresenceMatrix } from './PresenceMatrix.js';
import { EmployeeQuickEdit } from './EmployeeQuickEdit.js';
import { EmployeeWeek } from './EmployeeWeek.js';
import { CleaningList } from './CleaningList.js';
import {
  coverageStatusLabel,
  dayRange,
  demandDraftError,
  demandDraftFromDay,
  demandNum,
  demandParams,
  fmtWindowTime,
  fmtDayLabel,
  groupEmployeesByTeam,
  punchIntervalsOf,
  punchWindowOf,
  shiftDay,
  shiftSummary,
  teamLabel,
  DEMAND_HOTEL_FIELDS,
  DEMAND_RESTAURANT_FIELDS,
  FEED_HINT,
  GUESTS_HINT,
  coversOf,
  type DemandDraft,
} from './logic.js';

// The « Jour » view (S4-core, D3): the desk's day board — expected load
// entry (the 60-second D+1 gesture), activity coverage computed against
// declared presence (per-day overrides D1), and the declared-vs-clocked
// reconciliation. Demand + overrides are the ONLY desk writes in the Équipe
// group (operational hotel data, not staff/payroll). Coverage logic:
// @lemontaigu/staff computeDayCoverage, shared with staff.*, the week view,
// and the future S4-alerts job.

// The board column lists and row mappers moved into the BoardStore adapter at
// ladder step 6 — this file held the third verbatim copy of each. The write
// path still names its own payload columns inline; it moves at step 7.

interface DayClockEntry {
  employeeId: string;
  clockIn: string;
  clockOut: string | null;
}

export function DayView({
  activities,
  qualifications,
  employees,
  initialDay,
  canAssign,
  showHours = false,
}: {
  activities: BoardActivity[];
  qualifications: ActivityQualification[];
  employees: Employee[];
  initialDay: string | null;
  /** bos@ (is_admin): may pin ★ assignments and remove any presence. reception@: declared status only (0045). */
  canAssign: boolean;
  /** Présence row: also fetch + show the day's actual worked hours per employee (2026-08-04, bos@ only). */
  showHours?: boolean;
}) {
  // The board's home day is TODAY (operator, 2026-07-20): the first question on
  // opening the board is whether the hotel is properly attended right now. The
  // D+1 demand gesture was the original default, but it is one « › » away and
  // this view is opened far more often to check the running day than to plan
  // tomorrow's load.
  const board = useBoard();
  const boardPresence = useBoardPresence();
  const boardDemand = useBoardDemand();
  const boardRealtime = useBoardRealtime();
  const staffingConfig = useStaffingConfig();
  const metricRegistry = useMetricRegistry();
  const timesheet = useTimesheet();
  // Applying the prévisionnel writes a per-day override — the same capability
  // the ✎ editor needs. Hidden rather than disabled when absent: a control that
  // can only fail is worse than no control (ladder §3.19).
  const canWriteBoard = useCapability(CAPABILITIES.RH_BOARD_WRITE);
  const [day, setDay] = useState(() => initialDay ?? todayIsoParis());
  const [boardDay, setBoardDay] = useState<BoardDay | null>(null);
  const [prevOccupancy, setPrevOccupancy] = useState<number | null>(null);
  const [overrides, setOverrides] = useState<ActivityOverride[]>([]);
  const [presences, setPresences] = useState<ActivityPresence[]>([]);
  const [absences, setAbsences] = useState<string[]>([]);
  const [clockEntries, setClockEntries] = useState<DayClockEntry[]>([]);
  const [hoursByDay, setHoursByDay] = useState<Map<string, Map<string, number>> | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState('');

  const [draft, setDraft] = useState<DemandDraft>(demandDraftFromDay(null));
  const [draftErr, setDraftErr] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [adjusting, setAdjusting] = useState<number | null>(null);
  const [adjustingUntimed, setAdjustingUntimed] = useState<number | null>(null);
  const [broadcastState, setBroadcastState] = useState<'idle' | 'sent'>('idle');
  const [focusEmployee, setFocusEmployee] = useState<Employee | null>(null);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  // Restaurant row autosaves (2026-08-01) — this drives its « Enregistré ✓ ».
  const [restaurantSaved, setRestaurantSaved] = useState(false);
  // ── The Remarque is saved by a BUTTON, not by typing (operator 2026-09-09) ──
  //
  // Every desk write fires a realtime « demand » event, which reloads the day
  // and calls `setDraft` — so autosaving on each keystroke rewrote the field
  // under the cursor every 700 ms. On a number that is a blink; on a sentence
  // it is a flicker you cannot type through.
  //
  // `noteDirty` also protects the text from SOMEONE ELSE's edit: a colleague
  // changing dîners reloads this view, and an unsaved remark must survive that.
  const [noteDirty, setNoteDirty] = useState(false);
  const noteDirtyRef = useRef(false);
  const [noteSaved, setNoteSaved] = useState(false);
  // The printable service sheet (0147). A panel rather than a route: it is
  // always ABOUT the day already on screen, and a route would make the date a
  // second thing to keep in step.
  const [showCleaning, setShowCleaning] = useState(false);
  // Staffing profiles, for the « prévisionnel » strip. Loaded ONCE (config, not
  // per-day data) and never blocks the board: a failure leaves the strip absent.
  const [staffingSnapshot, setStaffingSnapshot] = useState<StaffingConfigSnapshot | null>(null);
  const [metrics, setMetrics] = useState<MetricDef[]>([]);
  const [applyingForecast, setApplyingForecast] = useState<number | null>(null);

  const loadDay = useCallback(
    async (key: string) => {
      setErr('');
      setLoaded(false);
      const { fromIso, toIso } = dayRange(key);
      // UTC-anchored superset covering the 04:00 business-day overhang —
      // computeDayWorkedHours buckets exactly and the row reads only `key`.
      const { fromIso: hoursFromIso, toIso: hoursToIso } = businessDayFetchRange(key, shiftDay(key, 1));
      try {
        // A single day is `range(key, key)` — the port has no `day()` method
        // because every board query is a date range and one shape beats two.
        const [d, o, p, ab, c, prev, h] = await Promise.all([
          board.days(key, key),
          board.overrides(key, key),
          board.presence(key, key),
          board.absences(key, key),
          // The punch read is C3's TIMESHEET aggregate — its own port, not
          // more methods on BoardStore (the ranges differ: instants, half-open).
          timesheet.entriesBetween(fromIso, toIso),
          // Previous night's occupancy seeds the running chain (0041).
          board.occupancyOn(shiftDay(key, -1)),
          // Worked hours for the Présence row (bos@ only). A SECOND timesheet
          // read rather than a reuse of `c` above: that one is bounded by
          // dayRange (local midnight to midnight) and drives the Gantt's
          // punches, whereas computeDayWorkedHours buckets by BUSINESS day —
          // a 02:00 clock-in belongs to the previous one. Reusing `c` would
          // silently under-count any night shift running past midnight;
          // widening `c` instead would put phantom punches on the Gantt.
          showHours
            ? timesheet.entriesBetween(hoursFromIso, hoursToIso)
            : Promise.resolve([]),
        ]);
        const nextDay = d[0] ?? null;
        setBoardDay(nextDay);
        setPrevOccupancy(prev);
        // Keep an unsaved remark across a reload — the reload may be someone
        // else's write, and losing what is on screen to it is not acceptable.
        setDraft((cur) => {
          const fresh = demandDraftFromDay(nextDay);
          return noteDirtyRef.current ? { ...fresh, note: cur.note } : fresh;
        });
        setOverrides(o);
        setPresences(p);
        setAbsences(ab.map((r) => r.employeeId));
        setClockEntries(c);
        setHoursByDay(showHours ? computeDayWorkedHours(h) : undefined);
        setLoaded(true);
        setDraftErr('');
        setSaved(false);
        setAdjusting(null);
        setBroadcastState('idle');
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    },
    [board, timesheet, showHours],
  );

  // Staffing config is CONFIGURATION, not day data: load it once, and never let
  // it fail the board. Without it the prévisionnel strip simply does not render.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const snap = await staffingConfig.load();
        if (!cancelled) setStaffingSnapshot(snap);
      } catch {
        if (!cancelled) setStaffingSnapshot(null);
      }
      try {
        // Same bargain as the staffing config above: configuration, loaded once,
        // and never allowed to fail the board. Without it a badge falls back to
        // the bare demand number rather than the page failing to render.
        const defs = await metricRegistry.load();
        if (!cancelled) setMetrics(defs);
      } catch {
        if (!cancelled) setMetrics([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [staffingConfig, metricRegistry]);

  // Presence/absence-only refresh — does NOT reset the ✎ panel (setAdjusting),
  // so the operator can edit several cells in a row without it closing (0045).
  const reloadPresence = useCallback(
    async (key: string) => {
      try {
        const [p, ab] = await Promise.all([board.presence(key, key), board.absences(key, key)]);
        setPresences(p);
        setAbsences(ab.map((r) => r.employeeId));
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    },
    [board],
  );

  // Overrides-only refresh — keeps the ✎ panel open after « Enregistrer »
  // (loadDay would reset setAdjusting and close it), so the operator can keep
  // editing assignments/declarations on the same activity.
  const reloadOverrides = useCallback(
    async (key: string) => {
      try {
        setOverrides(await board.overrides(key, key));
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    },
    [board],
  );

  useEffect(() => {
    void loadDay(day);
  }, [day, loadDay]);

  // Live: presence taps, demand edits from another session, waivers — the
  // board tables are small; reloading the visible day is cheap.
  useEffect(() => {
    // A presence change reloads presence ONLY, so the ✎ panel survives someone
    // else's edit; a demand change reloads the day. That distinction is why the
    // port emits two signals rather than one (ports.ts, BoardChange).
    return boardRealtime.watchBoard((what) => {
      if (what === 'presence') void reloadPresence(day);
      else void loadDay(day);
    });
  }, [day, loadDay, reloadPresence, boardRealtime]);

  const specs = useMemo(
    () =>
      staffingSnapshot
        ? staffingConfigFromRows({
            profiles: staffingSnapshot.profiles,
            bands: staffingSnapshot.bands,
            posts: staffingSnapshot.posts,
            workContent: staffingSnapshot.workContent,
          }).specs
        : [],
    [staffingSnapshot],
  );

  // The metric registry (ADR-0015) — what an activity's badge and gate resolve
  // against. `profileOutputs` is what lets « à blanc équivalent » be a real
  // metric rather than a number the strip alone knows: the staffing profile
  // computes it, publishes it, and the badge reads it back by key.
  const metricCtx = useMemo(
    () => ({
      day: boardDay,
      profileOutputs: boardDay
        ? profileOutputsForDay({
            specs,
            day: boardDay,
            workload: workloadFromDay(boardDay),
            readyBy: checkInTime(boardDay.arrivals),
          })
        : undefined,
    }),
    [specs, boardDay],
  );
  const registry = useMemo(() => buildRegistry(metrics), [metrics]);

  const coverage = useMemo(
    () =>
      computeActivityCoverage({
        date: day,
        activities,
        overrides,
        day: boardDay,
        presences,
        currentEmployeeIds: employees.map((e) => e.id),
        absentEmployeeIds: absences,
        metrics: registry,
        metricContext: metricCtx,
      }),
    [day, activities, overrides, boardDay, presences, employees, absences, registry, metricCtx],
  );

  // The « Présence » matrix reduced to this day's single row (operator request
  // 2026-08-04): the same table the Semaine/Mois horizons render, so the desk
  // reads « qui est en repos aujourd'hui ? » straight above the Gantt without
  // switching horizon. Untimed activities carry a 00:00 placeholder window and
  // would read as a phantom morning third — excluded, as the week matrix does.
  const presenceByDay = useMemo(() => {
    const map = new Map<string, Map<string, DayPresence>>();
    map.set(
      day,
      computeActivityPresence({
        date: day,
        activities: activities.filter((a) => !a.untimed),
        overrides,
        presences,
        absentEmployeeIds: absences,
        currentEmployeeIds: employees.map((e) => e.id),
      }),
    );
    return map;
  }, [day, activities, overrides, presences, employees, absences]);

  // Untimed activities (« sans horaire ») have no hour-axis bar — pull them OUT
  // of the Gantt (and its gap count / broadcast) and render them as their own
  // chip-row section after it. Timed coverage drives everything Gantt-related.
  const timedCoverage = useMemo(
    () => ({
      ...coverage,
      activities: coverage.activities.filter((c) => !c.activity.untimed),
      gaps: coverage.gaps.filter((c) => !c.activity.untimed),
    }),
    [coverage],
  );
  const untimedCoverage = useMemo(() => coverage.activities.filter((c) => c.activity.untimed), [coverage]);

  // ── « Prévisionnel » (2026-08-01) ─────────────────────────────────────────
  // What the saved load implies for the room-cleaning shift. Read-only use of
  // the staffing config; the editor for it stays operator-only in bos.
  const forecasts = useMemo(() => {
    if (!boardDay || specs.length === 0) return [];
    const workload = workloadFromDay(boardDay);
    return dayForecasts({ specs, day: boardDay, workload, readyBy: checkInTime(boardDay.arrivals) });
  }, [boardDay, specs]);

  /** The EFFECTIVE planned window — override ⊕ default, i.e. what the Gantt draws. */
  const plannedWindowFor = useCallback(
    (activityId: number) => {
      const c = coverage.activities.find((x) => x.activity.id === activityId);
      return c ? { windowStart: c.windowStart, windowEnd: c.windowEnd } : null;
    },
    [coverage],
  );

  /**
   * Align the planned window to the forecast — a PER-DAY override, so the
   * activity's default (which every other day reads) is untouched. `required`
   * and `note` are carried over from any existing override rather than reset,
   * because this gesture is about the WINDOW only.
   */
  const applyForecast = async (f: DayForecast) => {
    setApplyingForecast(f.activityId);
    setErr('');
    const existing = overrides.find((o) => o.activityId === f.activityId);
    try {
      await boardDemand.saveOverride({
        activityId: f.activityId,
        date: day,
        windowStart: f.windowStart,
        windowEnd: f.windowEnd,
        required: existing?.required ?? true,
        note: existing?.note ?? null,
      });
      await loadDay(day);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setApplyingForecast(null);
    }
  };

  const employeesById = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const firstName = (id: string) => {
    const e = employeesById.get(id);
    return e ? e.nickname || `${e.firstName} ${e.lastName.charAt(0)}.` : '?';
  };

  /*
   * `saveHotel` lived here until M4 (2026-09-01) and is gone with the Hôtel
   * row's inputs. It wrote Départs / Arrivées / Occupation through
   * `saveDemand` and then propagated the occupancy chain forward. All three
   * are now the feed's (§6.1), and §6.3 records the operator retiring the
   * chain: "the chain is not needed anymore since all of these are inferred
   * from arrivals/departures".
   *
   * The note used to ride on that button. It now autosaves with « Dîners » —
   * see `autosaveDesk`. Losing it silently was the trap in removing this.
   */

  /**
   * DESK autosave (operator decision 2026-08-01 — no button; widened at M4).
   *
   * It covered the Restaurant row; since M4 it covers everything the desk still
   * owns, which is exactly `dîners` and the note. The Hôtel row lost its
   * « Enregistrer » button along with its editable fields, and the note used to
   * ride on that button — so without this widening the note would have become
   * unsavable, a silent regression hidden behind a feature.
   *
   * Still a PARTIAL write, and now structurally so: `board_save_desk_day`
   * cannot touch a feed-owned column even if asked.
   *
   * Since 2026-09-09 the NOTE no longer triggers this — it has its own button
   * (`saveNote`) because autosaving a sentence rewrote it under the cursor. It
   * is still SENT here, because the RPC writes dîners and note together and
   * omitting it would blank a remark every time someone touched a number.
   */
  /**
   * The Remarque's own save. Sends the note WITH the current dîners because
   * `board_save_desk_day` writes both together — sending a stale dîners here
   * would silently roll back a number the desk had just typed.
   */
  const saveNote = async () => {
    if (demandDraftError(draft)) return;
    try {
      await boardDemand.saveDeskDay({
        date: day,
        diners: demandNum(draft.diners),
        note: draft.note.trim() === '' ? null : draft.note,
      });
      setNoteDirty(false);
      noteDirtyRef.current = false;
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 1500);
    } catch (e) {
      setDraftErr(e instanceof Error ? e.message : String(e));
    }
  };

  const restaurantTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autosaveDesk = (next: DemandDraft) => {
    if (restaurantTimer.current) clearTimeout(restaurantTimer.current);
    restaurantTimer.current = setTimeout(() => {
      if (demandDraftError(next)) return; // don't persist a half-typed number
      void (async () => {
        try {
          await boardDemand.saveDeskDay({
            date: day,
            diners: demandNum(next.diners),
            note: next.note.trim() === '' ? null : next.note,
          });
          setRestaurantSaved(true);
          setTimeout(() => setRestaurantSaved(false), 1500);
        } catch (e) {
          setDraftErr(e instanceof Error ? e.message : String(e));
        }
      })();
    }, 700);
  };
  // Flush a pending autosave when the day changes or the view unmounts, so a
  // number typed and immediately navigated away from is not lost.
  useEffect(() => {
    return () => {
      if (restaurantTimer.current) clearTimeout(restaurantTimer.current);
    };
  }, [day]);

  const saveOverride = async (activityId: number, patch: { windowStart: string; windowEnd: string; required: boolean; note: string }) => {
    setBusy(true);
    setErr('');
    try {
      await boardDemand.saveOverride({
        activityId,
        date: day,
        windowStart: patch.windowStart === '' ? null : patch.windowStart,
        windowEnd: patch.windowEnd === '' ? null : patch.windowEnd,
        required: patch.required,
        note: patch.note.trim() === '' ? null : patch.note.trim(),
      });
      void reloadOverrides(day);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const clearOverride = async (override: ActivityOverride) => {
    setBusy(true);
    setErr('');
    try {
      await boardDemand.clearOverride(override.id);
      void reloadOverrides(day);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const activityWindow = (activityId: number): { start: string; end: string } => {
    const a = activities.find((x) => x.id === activityId);
    const o = overrides.find((x) => x.activityId === activityId);
    return {
      start: o?.windowStart ?? a?.windowStart ?? '00:00',
      end: o?.windowEnd ?? a?.windowEnd ?? '00:00',
    };
  };

  // One activity-presence row (ADR-0011): (activity, employee, date) + status +
  // source. Idempotent on the unique key. Since step 7 it also lifts the day's
  // absence in the same transaction — the desk declaring someone can no longer
  // leave them both present and off.
  const upsertPresence = (
    activityId: number,
    employeeId: string,
    status: 'declared' | 'assigned',
    source: 'desk' | 'clock' | 'operator',
    declaredBy: string | null,
  ) => boardPresence.declarePresence({ activityId, employeeId, date: day, status, source, declaredBy });
  const deletePresence = (activityId: number, employeeId: string) =>
    boardPresence.withdrawPresence(activityId, employeeId, day);

  // bos@ ★ assignment (status='assigned', operator override — covers regardless of qualification).
  const assignEmployee = async (activityId: number, employeeId: string) => {
    setBusy(true);
    setErr('');
    try {
      await upsertPresence(activityId, employeeId, 'assigned', 'operator', null);
      void reloadPresence(day);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const unassignEmployee = async (activityId: number, employeeId: string) => {
    setBusy(true);
    setErr('');
    try {
      await deletePresence(activityId, employeeId);
      void reloadPresence(day);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Declared presence on an activity (ADR-0011): ONE row, status='declared'.
  // source='desk'; declared_by = the « via » receptionist (reception@) or null
  // (bos@). RLS gates who may write/delete.
  const declareOnActivity = async (activityId: number, employeeId: string, via: string | null) => {
    setBusy(true);
    setErr('');
    try {
      await upsertPresence(activityId, employeeId, 'declared', 'desk', via);
      void reloadPresence(day);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const undeclareOnActivity = async (activityId: number, employeeId: string) => {
    setBusy(true);
    setErr('');
    try {
      // reception@ RLS only deletes its own desk/clock declared rows; bos@ any.
      await deletePresence(activityId, employeeId);
      void reloadPresence(day);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Auto-declare from the clock (ADR-0011 §8): for each present (clocked) but
  // not-yet-on-the-board employee, add clock rows for every ACTIVITY they're
  // qualified for whose window overlaps their punch. Never touches someone who
  // already has any activity row. source='clock' (operator guess), editable.
  const autoDeclareFromClock = async () => {
    const activeActs = activities.filter((a) => a.active);
    const onBoard = new Set(presences.map((p) => p.employeeId));
    const absent = new Set(absences);
    const byEmp = new Map<string, DayClockEntry[]>();
    for (const e of clockEntries) byEmp.set(e.employeeId, [...(byEmp.get(e.employeeId) ?? []), e]);

    const rows: { activityId: number; employeeId: string }[] = [];
    for (const [empId, entries] of byEmp) {
      if (onBoard.has(empId) || absent.has(empId)) continue;
      const w = punchWindowOf(entries);
      if (!w) continue;
      const punchSlots = new Set(slotsOverlappingWindow(w.start, w.end));
      const qualified = new Set(qualifications.filter((q) => q.employeeId === empId).map((q) => q.activityId));
      for (const a of activeActs) {
        if (!qualified.has(a.id)) continue;
        const { start, end } = activityWindow(a.id);
        if (slotsOverlappingWindow(start, end).some((s) => punchSlots.has(s))) rows.push({ activityId: a.id, employeeId: empId });
      }
    }
    if (rows.length === 0) {
      setErr('Aucun présent à déclarer (déjà sur le tableau, ou aucun pointage exploitable pour une activité qualifiée).');
      return;
    }
    setBusy(true);
    setErr('');
    // Stop at the FIRST failure and say how far it got. The previous loop kept
    // only the last iteration's error, so a middle failure followed by a
    // success reported success over a half-applied batch.
    let done = 0;
    try {
      for (const r of rows) {
        await upsertPresence(r.activityId, r.employeeId, 'declared', 'clock', null);
        done++;
      }
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      setErr(`${done}/${rows.length} déclaration(s) enregistrée(s) puis échec : ${detail}`);
    } finally {
      setBusy(false);
      void reloadPresence(day);
    }
  };

  // Per-activity ⚡: attach a present person's shift to THIS activity as a
  // clock-sourced declared row (flagged apart from ★ and a typed desk row).
  const autoDeclareOnActivity = async (activityId: number, employeeId: string) => {
    setBusy(true);
    setErr('');
    try {
      await upsertPresence(activityId, employeeId, 'declared', 'clock', null);
      void reloadPresence(day);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Employees whose punch overlaps this activity's window, qualified for it,
  // and not already covering (same set the Gantt shows) — the ⚡ candidates.
  const autoCandidatesFor = (activityId: number): string[] => {
    const { start, end } = activityWindow(activityId);
    const actSlots = new Set(slotsOverlappingWindow(start, end));
    if (actSlots.size === 0) return [];
    const qualified = new Set(qualifications.filter((q) => q.activityId === activityId).map((q) => q.employeeId));
    const cov = coverage.activities.find((x) => x.activity.id === activityId);
    const covering = new Set([...(cov?.assigned ?? []), ...(cov?.coveredBy ?? [])]);
    const byEmp = new Map<string, DayClockEntry[]>();
    for (const e of clockEntries) byEmp.set(e.employeeId, [...(byEmp.get(e.employeeId) ?? []), e]);
    const out: string[] = [];
    for (const [empId, entries] of byEmp) {
      if (!qualified.has(empId) || covering.has(empId)) continue;
      const w = punchWindowOf(entries);
      if (!w) continue;
      if (slotsOverlappingWindow(w.start, w.end).some((s) => actSlots.has(s))) out.push(empId);
    }
    return out;
  };

  const broadcast = async () => {
    const gaps = timedCoverage.gaps;
    if (gaps.length === 0) return;
    const summary = gaps.map((g) => g.activity.label).join(', ');
    if (
      !window.confirm(
        `Diffuser les postes à pourvoir ${frDayLabel(day)} (${summary}) dans le canal Staff ? Tous les téléphones recevront une notification.`,
      )
    )
      return;
    setBusy(true);
    setErr('');
    try {
      await broadcastGaps(day, gaps);
      setBroadcastState('sent');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const today = todayIsoParis();
  const isPastOrToday = day <= today;
  const clockByEmployee = new Map<string, DayClockEntry[]>();
  for (const entry of clockEntries) {
    const list = clockByEmployee.get(entry.employeeId) ?? [];
    list.push(entry);
    clockByEmployee.set(entry.employeeId, list);
  }
  // Each clocked employee's punches AS THEY HAPPENED — the Gantt draws one
  // segment per interval against the single planned activity bar (ADR-0011 §8).
  const punchIntervalsByEmployee = new Map<string, ReturnType<typeof punchIntervalsOf>>();
  for (const [id, entries] of clockByEmployee) punchIntervalsByEmployee.set(id, punchIntervalsOf(entries));
  // Activity rows per employee — the presence list shows which activities each
  // person is on (labels), not slots.
  const presencesByEmployee = new Map<string, ActivityPresence[]>();
  for (const p of presences) presencesByEmployee.set(p.employeeId, [...(presencesByEmployee.get(p.employeeId) ?? []), p]);
  const absentSet = new Set(absences);
  const activityLabel = new Map(activities.map((a) => [a.id, a.label]));
  const chipColors = assignEmployeeColors(employees.map((e) => e.id));
  // Punch-derived rows (source='clock') — flagged ⚡ apart from ★ assigned and a typed declaration.
  const clockDeclaredIds = new Set(presences.filter((p) => p.source === 'clock').map((p) => p.employeeId));
  // Provenance of the (activity, employee) row for the declare-editor chips.
  const provenanceFor = (activityId: number, employeeId: string): string => {
    const p = presences.find((x) => x.activityId === activityId && x.employeeId === employeeId);
    if (!p || p.status === 'assigned') return '';
    if (p.source === 'tap') return '📱';
    if (p.source === 'message') return '💬';
    if (p.source === 'clock') return '⚡';
    if (p.source === 'desk' && p.declaredBy) {
      const e = employees.find((x) => x.id === p.declaredBy);
      return e ? `via ${e.firstName}` : 'via …';
    }
    return '';
  };

  // No, Na, Nd are all editable facts; Nr = No − Na is the only derived one
  // (algorithm locked 2026-08-01). The chained value is a FALLBACK, used only
  // when this day has no occupation entered yet.
  const arrivalsN = demandNum(draft.arrivals);
  const suggestedOcc = nextOccupancy(prevOccupancy, arrivalsN, demandNum(draft.departures));
  const effectiveOcc = draft.occupancy.trim() !== '' ? Number(draft.occupancy.trim()) : suggestedOcc;
  const recouchesRaw = effectiveOcc != null ? effectiveOcc - arrivalsN : null;
  const recouchesN = recouchesRaw != null ? Math.max(0, recouchesRaw) : null;
  // Consistency check, not a correction prompt: Nr can be read two ways —
  // No(d) − Na(d) (what we show) and No(d-1) − Nd(d) (the chain). They must
  // agree; when they don't, one of the three entered figures is wrong and
  // nothing else in the UI would ever say so.
  /**
   * The day's headline figures, one cell per flagged metric, evaluated against
   * the LIVE draft counts (operator request 2026-08-01, generalised by ADR-0015
   * « afficher en résumé ») so they move as the desk types.
   *
   * Replaces the single hard-coded « Charge » cell. `derivedOnly`: Départs,
   * Arrivées and the rest are the inputs immediately beside this — repeating
   * one as a derived cell would be noise.
   */
  const summaryDefs = useMemo(() => summaryMetrics(registry, { derivedOnly: true }), [registry]);
  const summaryCells = useMemo(() => {
    if (summaryDefs.length === 0) return [];
    const provisional = draftBoardDay({
      date: day,
      departures: demandNum(draft.departures),
      arrivals: arrivalsN,
      // The same effective occupancy the Recouches readout uses, so the
      // summary can never quote a different night's rooms than the row above it.
      occupancy: effectiveOcc ?? null,
      breakfasts: demandNum(draft.breakfasts),
      halfBoards: demandNum(draft.halfBoards),
      diners: demandNum(draft.diners),
    });
    const ctx = {
      day: provisional,
      profileOutputs: profileOutputsForDay({
        specs,
        day: provisional,
        workload: workloadFromDay(provisional),
        readyBy: checkInTime(provisional.arrivals),
      }),
    };
    const memo = new Map<string, number | null>();
    return summaryDefs.map((def) => {
      const value = evaluateMetric(registry, def.key, ctx, memo).value;
      return {
        key: def.key,
        label: def.labelFr,
        // The NUMBER alone — « 26,1 », not « 26,1 à-blancs éq. » (operator,
        // 2026-09-02). The unit is in the label above the box (« Charge
        // Ménage » is measured in à-blancs; that is what the metric IS), and
        // repeating it inside the field made this the one cell on the row
        // wide enough to break the alignment. The Mois cell keeps the suffix,
        // because it shows no labels and the unit is all it has.
        text: value === null ? null : formatMetricValue(value, def.unit),
      };
    });
  }, [summaryDefs, registry, specs, day, draft, arrivalsN, effectiveOcc]);

  /**
   * The people row — heads for each of the four room figures above it
   * (operator, 2026-09-02: *"on a separate row below the rooms related metrics,
   * to display the number of people for each metric Départ, arrivée,
   * occupation, recouche"*).
   *
   * « 10P (2E) » started inside the Occupation box and moved out here with the
   * rest: rooms and heads are two readings of the same night, and reading them
   * as a column each is what lets « 22 départs / 41P » be compared at a glance
   * with « 8 arrivées / 19P ».
   *
   * Read off `boardDay`, never the draft, because nothing here is typed. The
   * four columns are feed-owned (0129) and NULLABLE precisely so "no feed has
   * written this day" stays distinguishable from "no guests" — a `?? 0` would
   * put a confident « 0P » on every day before the feed existed, which is the
   * silent wrongness this whole feature was built to remove. So the row is
   * ABSENT until adults are known, and a cell the feed left null shows « — ».
   *
   * Recouches has no column of its own: it is `guests − arrivalsGuests`, the
   * people form of the same `Nr = No − Na` the rooms row derives (0041), and
   * clamped the same way. Children are shown only on Occupation — the source
   * under-reports them (§4.4) and they are flagged, never imputed, so the
   * figure is a floor, which the tooltip says.
   */
  const peopleCells = useMemo(() => {
    if (boardDay?.guestsAdults == null) return null;
    const kids = boardDay.guestsChildren;
    const guests = boardDay.guestsAdults + (kids ?? 0);
    const arr = boardDay.arrivalsGuests;
    // The bare number, no « P » suffix (operator, 2026-09-05: « 54P 67P (7E) »
    // → « 54  67 (7 E) »). The row label already says « Personnes », so the
    // suffix restated it four times per row; « (7 E) » keeps its letter because
    // that cell alone carries two figures and nothing else distinguishes them.
    const heads = (n: number | null | undefined): string => (n == null ? '—' : String(n));
    return [
      { key: 'departures', text: heads(boardDay.departuresGuests) },
      { key: 'arrivals', text: heads(arr) },
      {
        key: 'occupancy',
        text: kids != null && kids > 0 ? `${guests} (${kids} E)` : String(guests),
      },
      { key: 'stayovers', text: arr == null ? '—' : heads(Math.max(0, guests - arr)) },
    ];
  }, [boardDay]);

  const chainRecouches = prevOccupancy != null ? prevOccupancy - demandNum(draft.departures) : null;
  const recouchesMismatch =
    recouchesN != null && chainRecouches != null && recouchesN !== chainRecouches ? chainRecouches : null;

  // The ménage sheet REPLACES the board while it is open, rather than sitting
  // over it in a modal. `window.print()` prints the document, so anything still
  // rendered is on the paper — a Gantt behind a dialog would come out too.
  //
  // « Effectifs » used to work the same way and is now a nav item of its own
  // (operator, 2026-09-12) — a page, with its own day picker, in the place the
  // people who print it look for it. See `ServiceLists.tsx`.
  if (showCleaning) return <CleaningList day={day} onClose={() => setShowCleaning(false)} />;

  return (
    <div className="planning-board">
      <div className="staff-daybar">
        <button type="button" onClick={() => setDay(shiftDay(day, -1))} aria-label="Jour précédent">
          ‹
        </button>
        <FrDateInput className="pt-datepicker" value={day} onChange={(v) => v && setDay(v)} />
        <button type="button" onClick={() => setDay(shiftDay(day, 1))} aria-label="Jour suivant">
          ›
        </button>
        <span className="staff-daylabel">{fmtDayLabel(day)}</span>
        {day !== shiftDay(today, 1) && (
          <button type="button" className="staff-today" onClick={() => setDay(shiftDay(today, 1))}>
            Demain
          </button>
        )}
        {day !== today && (
          <button type="button" className="staff-today" onClick={() => setDay(today)}>
            Aujourd’hui
          </button>
        )}
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {!loaded && !err && <p className="staff-empty">Chargement…</p>}

      {loaded && (
        <>
          <section className="board-demand">
            {/* « Charge » + the feed's own freshness, 2026-09-02. The stamp was
                on the HÔTEL row label; it belongs on the title, because it
                qualifies every figure in the section and not just the first
                row. « prévue » went with it — the figures are imported now, not
                forecast. « N chambre·s à refaire » was removed: it restated
                Départs, which is the first cell directly below it. */}
            <h3 className="board-section-title">
              Charge
              <FeedStamp />
              {!coverage.demandEntered && <span className="board-tag board-tag-warn">non saisie</span>}
            </h3>
            {/* Field order locked by the operator 2026-08-01: Départs ·
                Arrivées · Occupation · Recouches(calculé).
                M4 (2026-09-01): the first three are now WRITTEN BY THE VEGA
                FEED and are read-only — §6.2. They render as `is-derived`
                <output> cells, the same affordance Recouches and Couverts
                already use, rather than as disabled inputs: a greyed-out box
                still reads as "something you could type into if you had the
                right", which is not what this is. The row's « Enregistrer »
                button goes with them — there is nothing left on it to save. */}
            <div className="board-demand-row">
              <span className="board-demand-rowlabel">Hôtel</span>
              {DEMAND_HOTEL_FIELDS.map((f) => (
                <div
                  key={f.key}
                  className="board-demand-cell is-derived is-fed"
                  style={{ '--metric-color': metricColor(f.key) } as CSSProperties}
                  title={FEED_HINT}
                >
                  <span>{f.label}</span>
                  <output>{draft[f.key] === '' ? '—' : draft[f.key]}</output>
                </div>
              ))}
              <div
                className="board-demand-cell is-derived"
                style={{ '--metric-color': metricColor('stayovers') } as CSSProperties}
              >
                <span>Recouches</span>
                <output>{recouchesN ?? '—'}</output>
              </div>
              {summaryCells.map((cell) => (
                <div
                  key={cell.key}
                  className="board-demand-cell is-derived"
                  style={{ '--metric-color': metricColor(cell.key) } as CSSProperties}
                  title={cell.label}
                >
                  <span>{cell.label}</span>
                  <output>{cell.text ?? '—'}</output>
                </div>
              ))}
              {/* The étage's sheet, on the HÔTEL row (operator, 2026-09-10):
                  it is a list of ROOMS, so it belongs beside the room figures
                  and not beside the covers. */}
              <div className="board-demand-cell board-demand-rowsave">
                <button type="button" onClick={() => setShowCleaning(true)}>
                  Ménage…
                </button>
              </div>
            </div>

            {/* Heads under rooms — one cell per figure above, same widths, so
                the two rows read as columns. No labels of its own: the row
                label says « Personnes » and the column headings are directly
                above. Absent entirely when the feed has written no people for
                this day, rather than a row of dashes on every pre-feed date. */}
            {peopleCells && (
              <div className="board-demand-row is-people" title={GUESTS_HINT}>
                <span className="board-demand-rowlabel">Personnes</span>
                {peopleCells.map((c) => (
                  <div
                    key={c.key}
                    className="board-demand-cell is-derived"
                    style={{ '--metric-color': metricColor(c.key) } as CSSProperties}
                  >
                    <output>{c.text}</output>
                  </div>
                ))}
              </div>
            )}

            <div className="board-demand-row is-restaurant">
              <span className="board-demand-rowlabel">Restaurant</span>
              {/* Five of the six are fed (0146). « Dîners Ext » is the ONE
                  surviving manual input — not because Vega has nothing to say
                  about it, but because what it says is a FLOOR: a walk-in IS a
                  restaurant booking, yet the desk works from a printout and
                  mostly does not enter one (15 in two years). The fed count is
                  shown beside the field as « n réservés » so the gap between
                  the two is visible instead of assumed (Q7 = A). */}
              {DEMAND_RESTAURANT_FIELDS.map((f) =>
                f.feedOwned ? (
                  <div
                    key={f.key}
                    className="board-demand-cell is-derived is-fed"
                    style={{ '--metric-color': metricColor(f.key) } as CSSProperties}
                    title={FEED_HINT}
                  >
                    <span>{f.label}</span>
                    <output>{draft[f.key] === '' ? '—' : draft[f.key]}</output>
                  </div>
                ) : (
                  <label
                    key={f.key}
                    className="board-demand-cell"
                    style={{ '--metric-color': metricColor(f.key) } as CSSProperties}
                  >
                    {/* A <span>, not a bare text node: the label band is a
                        fixed-height grid row whose content is bottom-aligned,
                        and a text node cannot be a grid item to align. Left
                        bare, « Dîners » floated a line above every label
                        beside it. */}
                    <span>{f.label}</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={draft[f.key]}
                      placeholder="0"
                      onChange={(e) => {
                        const next = { ...draft, [f.key]: e.target.value };
                        setDraft(next);
                        autosaveDesk(next);
                      }}
                    />
                    {/* The feed's floor, under the field the desk types into.
                        Hidden at zero: « 0 réservés » on every quiet evening
                        would train the eye to ignore the line on the one
                        evening it matters. */}
                    {f.key === 'diners' && (boardDay?.dinersExternalBooked ?? 0) > 0 && (
                      <span
                        className="board-demand-hint"
                        title={
                          'Réservations extérieures enregistrées dans Vega. Un ' +
                          'PLANCHER, pas un total : les réservations prises au ' +
                          'comptoir ou par téléphone n’y sont pas toujours saisies.'
                        }
                      >
                        {boardDay?.dinersExternalBooked} réservés
                      </span>
                    )}
                  </label>
                ),
              )}
              <div
                className="board-demand-cell is-derived"
                style={{ '--metric-color': metricColor('covers') } as CSSProperties}
              >
                <span>Couverts</span>
                <output>{coversOf(draft)}</output>
              </div>
              <div className="board-demand-cell board-demand-rowsave">
                {restaurantSaved && <span className="board-saved">Enregistré ✓</span>}
                {/* « Effectifs » lived here until 2026-09-12 — beside the
                    counts it totals, so « how many » and « who » were one click
                    apart. It is now its own nav item (Opérations on bos.*, a
                    top-level link on reception.*), because it grew a day of its
                    own: the sheet pairs a night's dinner with the NEXT
                    morning's breakfast, which is not the board's single date
                    and cannot be driven from the board's picker. */}
              </div>
            </div>

            <div className="board-demand-row">
              {/* « Remarque » sits in the LABEL column, like « Hôtel » and
                  « Restaurant » (operator, 2026-09-10), so its field lines up
                  with the fields above instead of starting a column to the
                  left of them. */}
              <span className="board-demand-rowlabel">Remarque</span>
              <label className="board-demand-cell board-demand-note">
                <input
                  type="text"
                  value={draft.note}
                  onChange={(e) => {
                    setDraft({ ...draft, note: e.target.value });
                    setNoteDirty(true);
                    noteDirtyRef.current = true;
                    setNoteSaved(false);
                  }}
                />
              </label>
              <div className="board-demand-cell board-demand-rowsave">
                <button type="button" onClick={() => void saveNote()} disabled={!noteDirty}>
                  Enregistrer
                </button>
                {noteDirty && <span className="board-unsaved">non enregistré</span>}
                {noteSaved && !noteDirty && <span className="board-saved">Enregistré ✓</span>}
              </div>
            </div>

            {/* The « Recouches = occupation − arrivées · Couverts = … » line was
                removed 2026-09-02. It taught the two derivations back when the
                desk TYPED their inputs and could get them wrong; after M4 the
                feed writes all four, so it restated arithmetic nobody performs
                on figures nobody enters. The rules still live where they are
                enforced — the derivation in `board_save_desk_day` (0131), the
                explanation in `docs/OCCUPANCY-CHAIN.md` — and the consistency
                warnings below still fire, which is the part that ever mattered.
                The seed prompt goes with it: an unseeded chain cannot happen on
                a fed board. */}
            {/* The two readings of Nr must agree; when they don't, one of the
                three entered figures is wrong and nothing else would say so. */}
            {recouchesMismatch != null && (
              <p className="board-demand-correction">
                Incohérence : d’après l’occupation d’hier ({prevOccupancy}) moins {demandNum(draft.departures)} départ·s,
                les recouches seraient {recouchesMismatch} — et non {recouchesN}. Vérifiez départs, arrivées ou
                occupation.
              </p>
            )}
            {recouchesRaw != null && recouchesRaw < 0 && (
              <p className="board-demand-correction">
                Occupation ({effectiveOcc}) inférieure aux arrivées ({arrivalsN}) — recouches ramenées à 0.
              </p>
            )}
            {draftErr && <p className="auth-err">{draftErr}</p>}
          </section>

          <PresenceMatrix
            employees={employees}
            days={[day]}
            presenceByDay={presenceByDay}
            today={today}
            hoursByDay={hoursByDay}
            title="Présence du jour"
          />

          <section>
            <h3 className="board-section-title">
              Activités
              {timedCoverage.gaps.length > 0 ? (
                <span className="board-tag board-tag-gap">
                  {timedCoverage.gaps.length} à pourvoir
                </span>
              ) : (
                <span className="board-tag board-tag-ok">rien à pourvoir</span>
              )}
              {timedCoverage.gaps.length > 0 && broadcastState === 'idle' && (
                <button type="button" className="board-broadcast" disabled={busy} onClick={() => void broadcast()}>
                  📢 Diffuser les postes à pourvoir
                </button>
              )}
              {broadcastState === 'sent' && <span className="board-saved">Diffusé ✓</span>}
            </h3>
            <PrevisionnelStrip
              forecasts={forecasts}
              plannedFor={plannedWindowFor}
              hasOverride={(id) => overrides.some((o) => o.activityId === id)}
              onApply={(f) => void applyForecast(f)}
              applyingId={applyingForecast}
              canApply={canWriteBoard}
            />
            <DayGantt
              coverage={timedCoverage}
              employees={employees}
              adjustingId={adjusting}
              onAdjust={(id) => setAdjusting(adjusting === id ? null : id)}
              onChipClick={(id) => setFocusEmployee(employeesById.get(id) ?? null)}
              clockedInIds={new Set(clockByEmployee.keys())}
              autoDeclaredIds={clockDeclaredIds}
              punchIntervalsByEmployee={punchIntervalsByEmployee}
              markClockStatus={isPastOrToday}
            />
            {adjusting !== null &&
              (() => {
                const c = coverage.activities.find((x) => x.activity.id === adjusting);
                if (!c) return null;
                return (
                  <OverrideForm
                    coverage={c}
                    employees={employees}
                    qualifications={qualifications}
                    provenanceFor={(id) => provenanceFor(c.activity.id, id)}
                    declaredIds={c.coveredBy}
                    autoCandidateIds={autoCandidatesFor(c.activity.id)}
                    canAssign={canAssign}
                    busy={busy}
                    onAssign={(employeeId) => void assignEmployee(c.activity.id, employeeId)}
                    onUnassign={(employeeId) => void unassignEmployee(c.activity.id, employeeId)}
                    onDeclare={(employeeId, via) => void declareOnActivity(c.activity.id, employeeId, via)}
                    onUndeclare={(employeeId) => void undeclareOnActivity(c.activity.id, employeeId)}
                    onAutoDeclare={(employeeId) => void autoDeclareOnActivity(c.activity.id, employeeId)}
                    onSave={(patch) => void saveOverride(c.activity.id, patch)}
                    onClear={() => {
                      if (c.override) void clearOverride(c.override);
                    }}
                  />
                );
              })()}
          </section>

          {untimedCoverage.length > 0 && (
            <section>
              <h3 className="board-section-title">Activités sans horaire</h3>
              {/* Sign-up activities (« Téléphone ») — no window, so no bar, but
                  same visual language as the Activités Gantt above: one row per
                  activity, a ✎ to open its assign/declare editor (same editors
                  the Gantt's OverrideForm uses), and a chip per person signed up. */}
              <div className="gantt">
                <ul className="board-untimed">
                  {untimedCoverage.map((c) => {
                    const covering = [
                      ...c.assigned.map((id) => ({ id, assigned: true })),
                      ...c.coveredBy.map((id) => ({ id, assigned: false })),
                    ];
                    return (
                      <li
                        key={c.activity.id}
                        className={`gantt-row board-untimed-row team-${c.activity.team ?? 'none'}${adjustingUntimed === c.activity.id ? ' is-adjusting' : ''}`}
                      >
                        <div className="gantt-label board-untimed-head">
                          <span className="gantt-label-text">{c.activity.label}</span>
                          <button
                            type="button"
                            className="gantt-adjust-btn"
                            title="Gérer les inscriptions"
                            onClick={() => setAdjustingUntimed(adjustingUntimed === c.activity.id ? null : c.activity.id)}
                          >
                            {adjustingUntimed === c.activity.id ? '×' : '✎'}
                          </button>
                        </div>
                        <div className="board-untimed-chips">
                          {covering.length === 0 && <span className="board-assign-empty">personne</span>}
                          {covering.map(({ id, assigned }) => {
                            const e = employeesById.get(id);
                            const label = e ? chipLabel(e) : '?';
                            return (
                              <span
                                key={id}
                                className={`gantt-chip${assigned ? ' is-assigned' : ''}`}
                                style={{ background: chipColors.get(id) }}
                                title={e ? `${e.firstName} ${e.lastName}` : id}
                              >
                                {assigned && <span className="gantt-chip-star">★</span>}
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {adjustingUntimed !== null &&
                  (() => {
                    const c = untimedCoverage.find((x) => x.activity.id === adjustingUntimed);
                    if (!c) return null;
                    return (
                      <div className="board-override-form board-untimed-editors">
                        <div className="board-override-title">Gérer « {c.activity.label} »</div>
                        {canAssign && (
                          <AssignEditor
                            activityId={c.activity.id}
                            assigned={c.assigned}
                            employees={employees}
                            qualifications={qualifications}
                            busy={busy}
                            onAssign={(employeeId) => void assignEmployee(c.activity.id, employeeId)}
                            onUnassign={(employeeId) => void unassignEmployee(c.activity.id, employeeId)}
                          />
                        )}
                        <DeclareEditor
                          declaredIds={c.coveredBy}
                          provenanceFor={(id) => provenanceFor(c.activity.id, id)}
                          employees={employees}
                          assignedIds={c.assigned}
                          requireVia={!canAssign}
                          busy={busy}
                          onDeclare={(employeeId, via) => void declareOnActivity(c.activity.id, employeeId, via)}
                          onUndeclare={(employeeId) => void undeclareOnActivity(c.activity.id, employeeId)}
                        />
                      </div>
                    );
                  })()}
              </div>
            </section>
          )}

          <section>
            <h3 className="board-section-title">
              Présence déclarée{isPastOrToday ? ' vs pointages' : ''}
              {isPastOrToday && clockEntries.length > 0 && (
                <button
                  type="button"
                  className="board-broadcast"
                  disabled={busy}
                  title="Déclare les présents non encore déclarés d’après leurs pointages et les activités qualifiées"
                  onClick={() => void autoDeclareFromClock()}
                >
                  ⚡ Auto-déclarer les présents
                </button>
              )}
            </h3>
            {/* Sorted by team (2026-07-21): grouped in the canonical team order,
                alpha within each — matches the Gantt/legend team ordering. */}
            {groupEmployeesByTeam(employees).map((g) => (
              <div key={g.team ?? 'none'} className="board-presence-group">
                <h4 className={`board-presence-team team-${g.team ?? 'none'}`}>
                  {g.team ? teamLabel(g.team) : 'Autres'}
                </h4>
                <ul className="board-presence">
                  {g.members.map((e) => {
                    const rows = presencesByEmployee.get(e.id) ?? [];
                    const absent = absentSet.has(e.id);
                    const declaredLabel = rows.map((p) => activityLabel.get(p.activityId) ?? '?').join(', ');
                    const expectsWork = rows.length > 0 && !absent;
                    const clocked = clockByEmployee.get(e.id) ?? [];
                    const summary = shiftSummary(clocked);
                    const color = chipColors.get(e.id);
                    return (
                      <li key={e.id} className="board-presence-row">
                        {/* Same coloured pill as the Planning/Réalisé Gantt chips (chipLabel +
                            assignEmployeeColors) — one stable colour per person across every
                            board view, so the presence list and the pointage bars visually match. */}
                        {canAssign ? (
                          <button
                            type="button"
                            className="gantt-chip is-clickable board-presence-chip"
                            style={{ background: color }}
                            title="Régler repos / activités pour ce jour"
                            onClick={() => setEditingEmployee(e)}
                          >
                            {chipLabel(e)}
                          </button>
                        ) : (
                          <span className="gantt-chip board-presence-chip" style={{ background: color }}>
                            {chipLabel(e)}
                          </span>
                        )}
                        <span
                          className={`board-presence-slot${absent ? ' board-presence-repos' : declaredLabel ? '' : ' is-empty'}`}
                        >
                          {absent ? 'Repos' : declaredLabel || 'non déclaré'}
                          {clockDeclaredIds.has(e.id) && !absent && (
                            <span className="board-presence-auto" title="D’après le pointage">
                              {' '}
                              ⚡
                            </span>
                          )}
                        </span>
                        {isPastOrToday && (
                          <span className={`board-presence-clock${summary ? '' : ' is-empty'}`}>
                            {summary && <span className="gantt-erow-dot" style={{ background: color }} />}
                            {summary || (expectsWork ? 'aucun pointage' : '—')}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </section>

          {focusEmployee && (
            <EmployeeWeek
              employee={focusEmployee}
              color={chipColors.get(focusEmployee.id)}
              anchorDay={day}
              activities={activities}
              onClose={() => setFocusEmployee(null)}
            />
          )}

          {editingEmployee && (
            <EmployeeQuickEdit
              employee={editingEmployee}
              color={chipColors.get(editingEmployee.id)}
              anchorDay={day}
              activities={activities}
              qualifications={qualifications}
              onClose={() => setEditingEmployee(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

function OverrideForm({
  coverage,
  employees,
  qualifications,
  provenanceFor,
  declaredIds,
  autoCandidateIds,
  canAssign,
  busy,
  onAssign,
  onUnassign,
  onDeclare,
  onUndeclare,
  onAutoDeclare,
  onSave,
  onClear,
}: {
  coverage: ActivityCoverage;
  employees: Employee[];
  qualifications: ActivityQualification[];
  provenanceFor: (employeeId: string) => string;
  declaredIds: string[];
  autoCandidateIds: string[];
  canAssign: boolean;
  busy: boolean;
  onAssign: (employeeId: string) => void;
  onUnassign: (employeeId: string) => void;
  onDeclare: (employeeId: string, via: string | null) => void;
  onUndeclare: (employeeId: string) => void;
  onAutoDeclare: (employeeId: string) => void;
  onSave: (patch: { windowStart: string; windowEnd: string; required: boolean; note: string }) => void;
  onClear: () => void;
}) {
  const { activity, override } = coverage;
  const defStart = fmtWindowTime(activity.windowStart);
  const defEnd = fmtWindowTime(activity.windowEnd);
  // Prefill the effective window (override ⊕ default) so the operator tweaks
  // from real values, not blank inputs.
  const [windowStart, setWindowStart] = useState(fmtWindowTime(override?.windowStart ?? activity.windowStart));
  const [windowEnd, setWindowEnd] = useState(fmtWindowTime(override?.windowEnd ?? activity.windowEnd));
  const [required, setRequired] = useState(override ? override.required : true);
  const [note, setNote] = useState(override?.note ?? '');

  // A bound left at the default is stored as null (keeps the default; no false
  // « ajusté » marker) — only a genuine change persists a window override.
  const submit = () =>
    onSave({
      windowStart: windowStart === defStart ? '' : windowStart,
      windowEnd: windowEnd === defEnd ? '' : windowEnd,
      required,
      note,
    });

  return (
    <div className="board-override-form">
      <div className="board-override-title">
        Ajuster « {activity.label} » — par défaut {fmtWindowTime(activity.windowStart)}–
        {fmtWindowTime(activity.windowEnd)}
      </div>

      {/* bos@ only: the prescriptive ★ layer. reception@ never sees it. */}
      {canAssign && (
        <AssignEditor
          activityId={activity.id}
          assigned={coverage.assigned}
          employees={employees}
          qualifications={qualifications}
          busy={busy}
          onAssign={onAssign}
          onUnassign={onUnassign}
        />
      )}

      {/* Both roles: declared status. reception@ must attach a « via ». The ⚡
          candidates are present (clocked) qualified staff, one tap to declare
          them from their shift onto this activity. */}
      <DeclareEditor
        declaredIds={declaredIds}
        provenanceFor={provenanceFor}
        employees={employees}
        assignedIds={coverage.assigned}
        autoCandidateIds={autoCandidateIds}
        requireVia={!canAssign}
        busy={busy}
        onDeclare={onDeclare}
        onUndeclare={onUndeclare}
        onAutoDeclare={onAutoDeclare}
      />

      <label>
        Début
        <input type="time" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} />
      </label>
      <label>
        Fin
        <input type="time" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} />
      </label>
      <label className="board-override-check">
        <input type="checkbox" checked={!required} onChange={(e) => setRequired(!e.target.checked)} />
        Dispensé ce jour (0 présent acceptable)
      </label>
      <label className="board-override-note">
        Remarque
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex. peu d’arrivées" />
      </label>
      <div className="board-override-actions">
        <button type="button" disabled={busy} onClick={submit}>
          Enregistrer
        </button>
        {override && (
          <button type="button" disabled={busy} onClick={onClear}>
            Rétablir la valeur par défaut
          </button>
        )}
      </div>
    </div>
  );
}
